import React from 'react';
import { motion } from 'framer-motion';

const CRAFT_IMG = "https://static.prod-images.emergentagent.com/jobs/0aefe824-dd3a-42e7-88bf-f336a3227281/images/71d5c259f6c851459268b8cbc0e88d04f9129edb77e9d70905765b60fce0b655.png";
const ARTISAN_IMG = "https://static.prod-images.emergentagent.com/jobs/0aefe824-dd3a-42e7-88bf-f336a3227281/images/21f16657c96e3fc17e1622cdb3300552ed4e26ce78c8a54e84ccda2934e85891.png";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stats = [
  { num: '4–8 weeks', label: 'per piece', sub: 'Depending on stitch complexity' },
  { num: '36+', label: 'hand stitch types', sub: 'Murri, Phanda, Jaali, Bakhiya & more' },
  { num: 'Direct', label: 'karigar sourcing', sub: 'No middlemen. Fair pay, always.' },
];

export default function BrandStorySection() {
  return (
    <section id="craft" data-testid="about-craft" className="py-24 md:py-32 bg-[#F5F0E8] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-16 md:gap-20 items-center">

          {/* Left — Images */}
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="relative"
          >
            <div className="relative h-[420px] md:h-[560px]">
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src={CRAFT_IMG}
                  alt="Close-up of intricate Chikankari hand embroidery on white fabric"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, x: 30, y: 30 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.35, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="absolute -bottom-8 -right-6 w-48 md:w-60 h-56 md:h-72 overflow-hidden border-4 border-[#F5F0E8] shadow-xl"
              >
                <img
                  src={ARTISAN_IMG}
                  alt="Karigar artisan hand-stitching Chikankari in Lucknow"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </motion.div>
              <div className="absolute top-5 left-5 bg-[#2D4A2D] px-4 py-2">
                <span className="text-[10px] font-sans uppercase tracking-[0.22em] text-[#C9A84C]">Karigar-made. Always.</span>
              </div>
            </div>
          </motion.div>

          {/* Right — Story */}
          <motion.div
            variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.14 } } }}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
          >
            <motion.span variants={fadeUp} className="inline-block text-xs font-sans uppercase tracking-[0.25em] text-[#C9A84C] font-medium mb-4">
              Our Story
            </motion.span>

            <motion.h2 variants={fadeUp} className="font-serif font-light text-[#2D4A2D] text-4xl md:text-5xl leading-[1.1] tracking-tight mb-6">
              Not a brand.<br /><span className="italic">A belief.</span>
            </motion.h2>

            <motion.p variants={fadeUp} className="font-sans font-light text-[#3A4A3A] text-sm md:text-base leading-relaxed mb-4">
              We started Weaves of Lucknow because we were tired of explaining the difference between real Chikankari and the machine-stitched copies flooding every market. The authentic craft was disappearing — not because the artisans were gone, but because buyers couldn't tell the difference anymore.
            </motion.p>

            <motion.p variants={fadeUp} className="font-sans font-light text-[#3A4A3A] text-sm md:text-base leading-relaxed mb-4">
              Real Chikankari comes from Lucknow's old city — from karigar families in Aminabad, Chowk, and Nakhas who've been doing this for two or three generations. A suit set with murri, phanda, and jaali work takes four to eight weeks of continuous hand-stitching. The fabric is natural: cotton, Kota Doria, Chanderi, mal mal.
            </motion.p>

            <motion.p variants={fadeUp} className="font-sans font-light text-[#3A4A3A] text-sm md:text-base leading-relaxed mb-8">
              We source directly from these karigar families. No middlemen. Fair wages. When you wear our pieces, the woman who made them was paid what her time is worth — not a fraction of it.
            </motion.p>

            {/* Stats */}
            <motion.div variants={fadeUp} className="grid grid-cols-3 gap-4 pt-8 border-t border-[#D5CCBF]">
              {stats.map(({ num, label, sub }) => (
                <div key={label}>
                  <div className="font-serif text-xl md:text-2xl text-[#2D4A2D] font-light mb-0.5 leading-tight">{num}</div>
                  <div className="text-[10px] font-sans uppercase tracking-wider text-[#C9A84C] font-medium mb-1.5 leading-tight">{label}</div>
                  <div className="text-xs font-sans text-[#5C6B5C] leading-snug">{sub}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
